import React, { useEffect, useMemo, useRef } from 'react'

import { QueryClientProvider } from '@tanstack/react-query'
import { Navigate, Route, Routes, matchPath } from 'react-router-dom'

import { array, bool, object, shape, string } from 'prop-types'

import LazyAiSignInModal from 'Pods/LazyLoaded/AiSignInModal/LazyAiSignInModal'
import ContextBasedRouter, { useLocation } from 'Pods/Shared/ContextBasedRouter'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import {
  COMMUNITY_IMAGES_ROUTE,
  GALLERY_IMAGE_PATH_PATTERN,
  IMAGE_PATH_PATTERN,
  STUDIO_PATH,
  TWO_D_IMAGES_PATHS,
  hasImageEditParam,
} from 'Pods/ThreeDStudio/constants'
import { STUDIO_EVENT, trackStudioEvent } from 'Pods/ThreeDStudio/Services/studioTracker'
import { setGenerationProviderWeights } from 'Pods/ThreeDStudio/Shared/Utils/resolveInitialGenerationProvider'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

import queryClient from 'Utils/queryClient'
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

import BestOnDesktop from './Components/BestOnDesktop/BestOnDesktop'
import CommentsModal from './Components/CommentsModal/CommentsModal'
import FullImageEditor from './Components/FullImageEditor/FullImageEditor'
import ImageDeepLink from './Components/ImageDeepLink/ImageDeepLink'
import ImageGenerator from './Components/ImageGenerator/ImageGenerator'
import LazyCommunityGalleryPanel from './Components/LazyCommunityGalleryPanel/LazyCommunityGalleryPanel'
import LazyConferenceSignInModal from './Components/LazyConferenceSignInModal/LazyConferenceSignInModal'
import LazyImageGalleryPanel from './Components/LazyImageGalleryPanel/LazyImageGalleryPanel'
import LazyThreeDModelPicker from './Components/LazyThreeDModelPicker/LazyThreeDModelPicker'
import MainPanel from './Components/MainPanel/MainPanel'
import MobileTabBar from './Components/MobileTabBar/MobileTabBar'
import SettingsModal from './Components/SettingsModal/SettingsModal'
import Sidebar from './Components/Sidebar/Sidebar'
import UsageModal from './Components/UsageModal/UsageModal'
import ViewerSidebar from './Components/ViewerSidebar/ViewerSidebar'
import useActiveModelSchema from './Hooks/useActiveModelSchema'
import useAuthModalTrigger from './Hooks/useAuthModalTrigger'
import useCheckUnderControl from './Hooks/useCheckUnderControl'
import useClearReviewedOnOpen from './Hooks/useClearReviewedOnOpen'
import useCloseImageEditorOnNavigate from './Hooks/useCloseImageEditorOnNavigate'
import useDeepLinkAcquiredModel from './Hooks/useDeepLinkAcquiredModel'
import useDeepLinkGeneration from './Hooks/useDeepLinkGeneration'
import useGalleryOriginSync from './Hooks/useGalleryOriginSync'
import useHydrateGenerationQuota from './Hooks/useHydrateGenerationQuota'
import useInitialMobilePanel from './Hooks/useInitialMobilePanel'
import useLoadInitialCredits from './Hooks/useLoadInitialCredits'
import useModelUrlSync from './Hooks/useModelUrlSync'
import usePageTitleSync from './Hooks/usePageTitleSync'
import usePendingGeneratorRun from './Hooks/usePendingGeneratorRun'
import useResumePendingGeneration from './Hooks/useResumePendingGeneration'
import useStudioPageViewEvent from './Hooks/useStudioPageViewEvent'
import { closeSignInModal, closeThreeDModelPicker } from './Store/actions/ui'
import ThreeDStudioPageContext from './ThreeDStudioPageContext'

const EMPTY_FILTER_OPTIONS = {
  visibleCategories: EMPTY_ARRAY,
  sortOptions: EMPTY_ARRAY,
}

function ThreeDStudio({
  locale = 'en',
  initialPathname = '',
  generationProviderWeights = EMPTY_OBJECT,
  ...props
}) {
  // Assigned during render rather than in an effect: child effects run first, and the
  // generation schema effect rolls the initial engine on mount.
  setGenerationProviderWeights(generationProviderWeights)

  // route_translator serves the studio under a lowercase locale prefix (/pt/ai,
  // /zh-cn/ai). Mounting the router on that prefix keeps every in-app path
  // ('/ai', '/ai-3d-model-generator/3d-models') locale-agnostic while Link and
  // navigate() stay on the visitor's locale.
  const basename = locale === 'en' ? '/' : `/${locale.toLowerCase()}`

  return (
    <ContextBasedRouter basename={basename} initialPathname={initialPathname}>
      {/* ThreeDStudioContent is wrapped in a QueryClientProvider because the library
       (LibraryContent inside Sidebar) fetches its items via react-query. */}
      <QueryClientProvider client={queryClient}>
        <ThreeDStudioContent locale={locale} {...props} />
      </QueryClientProvider>
    </ContextBasedRouter>
  )
}

// Per-user values (userLoggedIn, the actor-scoped Flipper flags) arrive via
// usePageConfig - the same channel UnifiedNavigation uses - and not via SSR
// props, because this page is rendered by FastApplicationController and its HTML
// can be served from a shared cache. The exception is ideationFlowEnabled, which
// also ships as an actorless SSR prop so its entry points do not shift the layout
// on hydration. The library itself loads client-side from the authenticated
// /api/internal/generator/sessions endpoint (see useGetLibraryItems).
function ThreeDStudioContent({
  locale = 'en',
  extractingCoreStyles = false,
  initialRunSlug = null,
  acquiredItemSlug = null,
  dccSoftwares = EMPTY_ARRAY,
  communityGalleryFilterOptions = EMPTY_FILTER_OPTIONS,
  communityGalleryInitialModels = null,
  conferenceMode = null,
  ideationFlowEnabled: slowIdeationFlowEnabled = false,
  twoDImagesIdeationFlowEnabled: slowTwoDImagesIdeationFlowEnabled = false,
  multiViewEnabled: slowMultiViewEnabled = false,
  cgdreamOrigin = false,
}) {
  const pageConfig = usePageConfig({ listener: 'full-page-config-loaded', checkIfAvailable: false })
  const pageConfigLoaded = pageConfig !== undefined
  const enableHunyuanSegmentation = !!pageConfig?.enableHunyuanSegmentation
  const enableMeshyRemesh = !!pageConfig?.enableMeshyRemesh
  const enableAnimation = !!pageConfig?.enableAnimation
  const enableExtraQuality = !!pageConfig?.enableExtraQuality
  const enable2dRender = !!pageConfig?.enable2dRender

  useActiveModelSchema({ enableHunyuanSegmentation, enableMeshyRemesh })
  usePendingGeneratorRun()
  useHydrateGenerationQuota()
  useCheckUnderControl()
  useLoadInitialCredits()
  useGalleryOriginSync()
  useDeepLinkGeneration()
  useDeepLinkAcquiredModel(acquiredItemSlug)
  useModelUrlSync(initialRunSlug, acquiredItemSlug)
  usePageTitleSync()
  useClearReviewedOnOpen()
  useCloseImageEditorOnNavigate()
  useAuthModalTrigger()
  useResumePendingGeneration()
  useInitialMobilePanel({ extractingCoreStyles })

  const openedModelFileUrl = useThreeDStudioStore(
    state => !!state.ui?.openedModel?.outputAssets?.[0]?.fileUrl
  )
  const uploadedModelUrl = useThreeDStudioStore(
    state => state.modelSettings?.inputThreeDUrl
  )
  const isModelOpened = Boolean(openedModelFileUrl || uploadedModelUrl)

  const activeMobilePanel = useThreeDStudioStore(state => state.ui?.activeMobilePanel)
  const isImageEditorOpen = useThreeDStudioStore(state => Boolean(state.ui?.openedImage))
  const location = useLocation()
  const isImageEditRequested = hasImageEditParam(location?.search)
  const isSignInModalOpen = useThreeDStudioStore(state => state.ui?.isSignInModalOpen)
  const isThreeDModelPickerOpen = useThreeDStudioStore(state => state.ui?.isThreeDModelPickerOpen)
  const signInModalMode = useThreeDStudioStore(state => state.ui?.signInModalMode)
  const signInModalStep = useThreeDStudioStore(state => state.ui?.signInModalStep)
  const userLoggedIn = !!pageConfig?.isCurrentUser
  const showGenerationErrorDetails = !!pageConfig?.showGenerationErrorDetails
  const modelReadyToasterEnabled = !!pageConfig?.modelReadyToasterEnabled
  const segmentationFlowEnabled = !!pageConfig?.segmentationFlowEnabled
  const designerAssistEnabled = !!pageConfig?.designerAssistEnabled
  const modelPickerEnabled = !!pageConfig?.modelPickerEnabled
  const treeViewEnabled = !!pageConfig?.treeViewEnabled
  // SSR default from the actorless flag, refined in place once pageConfig lands with the
  // actor-scoped value. Rendering the ideation row server-side keeps it out of the
  // post-hydration layout shift (see the fast-page SSR-stability rule in CLAUDE.md).
  const ideationFlowEnabled = pageConfig?.ideationFlowEnabled ?? slowIdeationFlowEnabled
  // Same pattern again: the sidebar renders the layout the actorless SSR prop picked, and
  // pageConfig refines it per user. It also decides whether the 2D routes resolve at all - the
  // controller is a fast page, so a non-enrolled visitor is bounced here rather than server-side.
  const twoDImagesIdeationFlowEnabled = pageConfig?.twoDImagesIdeationFlowEnabled
    ?? slowTwoDImagesIdeationFlowEnabled
  const imageSearchOnIdeationFLowEnabled = !!pageConfig?.imageSearchOnIdeationFLowEnabled
  // Same SSR-default-then-refine pattern: the Multi-View tab is part of the server-rendered tab row.
  const multiViewEnabled = pageConfig?.multiViewEnabled ?? slowMultiViewEnabled

  useStudioPageViewEvent(STUDIO_EVENT.TOOL_VIEW, {
    enabled: pageConfigLoaded && !extractingCoreStyles,
  })

  const isRegistrationModalVisible = isSignInModalOpen && !userLoggedIn
  const hasTrackedSignInModalRef = useRef(false)

  useEffect(() => {
    if (!isRegistrationModalVisible) {
      hasTrackedSignInModalRef.current = false
      return
    }
    if (hasTrackedSignInModalRef.current) return
    hasTrackedSignInModalRef.current = true
    trackStudioEvent(STUDIO_EVENT.REGISTRATION_MODAL_VIEW, { mode: signInModalMode })
  }, [isRegistrationModalVisible, signInModalMode])

  const pageContextValue = useMemo(
    () => ({
      locale,
      userLoggedIn,
      cgdreamOrigin,
      pageConfigLoaded,
      extractingCoreStyles: !!extractingCoreStyles,
      dccSoftwares,
      showGenerationErrorDetails,
      modelReadyToasterEnabled,
      segmentationFlowEnabled,
      enableHunyuanSegmentation,
      enableMeshyRemesh,
      enableAnimation,
      enableExtraQuality,
      enable2dRender,
      designerAssistEnabled,
      modelPickerEnabled,
      treeViewEnabled,
      ideationFlowEnabled,
      twoDImagesIdeationFlowEnabled,
      imageSearchOnIdeationFLowEnabled,
      multiViewEnabled,
      communityGalleryFilterOptions,
      communityGalleryInitialModels,
    }),
    [
      locale,
      userLoggedIn,
      cgdreamOrigin,
      pageConfigLoaded,
      extractingCoreStyles,
      dccSoftwares,
      showGenerationErrorDetails,
      modelReadyToasterEnabled,
      segmentationFlowEnabled,
      enableHunyuanSegmentation,
      enableMeshyRemesh,
      enableAnimation,
      enableExtraQuality,
      enable2dRender,
      designerAssistEnabled,
      modelPickerEnabled,
      treeViewEnabled,
      ideationFlowEnabled,
      twoDImagesIdeationFlowEnabled,
      imageSearchOnIdeationFLowEnabled,
      multiViewEnabled,
      communityGalleryFilterOptions,
      communityGalleryInitialModels,
    ]
  )

  // An image opened from the library takes the whole area right of the sidebar, so the
  // generate form, the viewer and the viewer rail are not rendered at all rather than
  // hidden - and neither is the assistant panel, which MainPanel mounts. Nothing on that
  // surface opens the panel (124g39pbffa closes the editor first).
  const workspace = isImageEditorOpen ? <FullImageEditor /> : (
    <div className="three-d-studio__workspace" data-active-panel={activeMobilePanel}>
      <MobileTabBar isModelOpened={isModelOpened} />
      <MainPanel hasViewerSidebar />
      <ViewerSidebar isModelOpened={isModelOpened} />
    </div>
  )

  // The 2D routes exist only for the enrolled cohort; everyone else bounces to /ai. Within
  // the cohort, the community images URL shows the gallery and the image generator route the
  // full-width Create page, or the editor an image opened from it or the library. A single
  // image's URL renders the same page, and ImageDeepLink opens that image over it; a community
  // image opens in the gallery's own modal, or in the editor with `?edit=true`.
  const twoDImagesElement = (path) => {
    if (!twoDImagesIdeationFlowEnabled) return <Navigate to={STUDIO_PATH.GENERATE} replace />
    if (path === COMMUNITY_IMAGES_ROUTE) {
      const isGalleryImageEditRequested = isImageEditRequested
        && matchPath(GALLERY_IMAGE_PATH_PATTERN, location?.pathname ?? '') != null
      if (isGalleryImageEditRequested) return <FullImageEditor />

      return (
        <div className="three-d-studio__gallery-main">
          <LazyImageGalleryPanel />
        </div>
      )
    }
    // An image URL asking for the editor renders it straight away, empty until ImageDeepLink
    // has loaded the image, so the Create page never flashes first.
    const showsEditor = isImageEditorOpen || (path === IMAGE_PATH_PATTERN && isImageEditRequested)
    return showsEditor ? <FullImageEditor /> : <ImageGenerator />
  }

  return (
    <ThreeDStudioPageContext.Provider value={pageContextValue}>
      <div className="three-d-studio">
        <Sidebar />
        <Routes>
          <Route
            path={STUDIO_PATH.COMMUNITY_THREE_D_MODELS}
            element={(
              <div className="three-d-studio__gallery-main">
                <LazyCommunityGalleryPanel />
              </div>
            )}
          />
          {TWO_D_IMAGES_PATHS.map(path => (
            <Route key={path} path={path} element={twoDImagesElement(path)} />
          ))}
          <Route path="*" element={workspace} />
        </Routes>
        {/* Outside the routes so closing the lightbox to /ai-image does not unmount it and
            abort the regenerate request its Regenerate action just sent. */}
        {twoDImagesIdeationFlowEnabled && <ImageDeepLink />}
        <UsageModal />
        <SettingsModal />
        <CommentsModal />
        <BestOnDesktop />
        {conferenceMode && !userLoggedIn && (
          <LazyConferenceSignInModal eventName={conferenceMode.eventName} />
        )}
        {isSignInModalOpen && !userLoggedIn && (
          <LazyAiSignInModal mode={signInModalMode} step={signInModalStep} onClose={closeSignInModal} />
        )}
        {isThreeDModelPickerOpen && modelPickerEnabled && (
          <LazyThreeDModelPicker onClose={closeThreeDModelPicker} />
        )}
      </div>
    </ThreeDStudioPageContext.Provider>
  )
}

ThreeDStudio.propTypes = {
  locale: string,
  generationProviderWeights: object,
  extractingCoreStyles: bool,
  ideationFlowEnabled: bool,
  twoDImagesIdeationFlowEnabled: bool,
  multiViewEnabled: bool,
  cgdreamOrigin: bool,
  initialPathname: string,
  initialRunSlug: string,
  acquiredItemSlug: string,
  dccSoftwares: array,
  communityGalleryFilterOptions: shape({
    visibleCategories: array,
    sortOptions: array,
  }),
  communityGalleryInitialModels: shape({
    data: array,
    meta: object,
  }),
  conferenceMode: shape({
    eventName: string.isRequired,
  }),
}

ThreeDStudioContent.propTypes = {
  locale: string,
  extractingCoreStyles: bool,
  ideationFlowEnabled: bool,
  twoDImagesIdeationFlowEnabled: bool,
  multiViewEnabled: bool,
  cgdreamOrigin: bool,
  initialRunSlug: string,
  acquiredItemSlug: string,
  dccSoftwares: array,
  communityGalleryFilterOptions: shape({
    visibleCategories: array,
    sortOptions: array,
  }),
  communityGalleryInitialModels: shape({
    data: array,
    meta: object,
  }),
  conferenceMode: shape({
    eventName: string.isRequired,
  }),
}

export default ThreeDStudio
